/**
 * OpenPopup.tsx — Renders a trigger element that opens a MUI `<Dialog>` modal.
 *
 * JSON trigger: `uiCompType.method: "renderOpenPopup"`
 * Also callable programmatically via `window.openPopup(sectionData)`.
 *
 * **Trigger element:** Renders `sectionData` content (title, button, image, etc.)
 * as the clickable trigger. Any nested `SectionData` is dispatched via `renderSection`.
 *
 * **Dialog content:** Determined by `sectionData.contentMethod`:
 *  - If set, the component at the path `sectionData.component` is dynamically
 *    imported via `next/dynamic` and rendered inside the dialog.
 *  - Otherwise, `sectionData.panels` are rendered as `Panel` children.
 *
 * **Key data fields:**
 *  - `sectionData.title`         — Dialog title bar text
 *  - `sectionData.contentMethod` — Dynamic import trigger
 *  - `sectionData.component`     — Path for `next/dynamic` import
 *  - `sectionData.input`         — Props forwarded to the dynamic component
 *  - `sectionData.dialogProps`   — Spread onto the MUI `<Dialog>` component
 *
 * **Style slots:** `uiCompType.styles` (dialog container).
 */
import React, { useState } from "react";
import { Box, Dialog, DialogTitle, DialogContent, IconButton } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import dynamic from "next/dynamic";
import { SectionData, ComponentRenderProps, renderSection } from "./index";
import {transformDescription} from "./shared";
import Title from './Title';
import SubTitle from './SubTitle';

const OpenPopup: React.FC<{ sectionData: SectionData; props: ComponentRenderProps }> = ({ sectionData, props }) => {
    const [modalOpen, setModalOpen] = useState(true);
    const [LoadedComponent, setLoadedComponent] = useState<React.ComponentType<any> | null>(null);

    React.useEffect(() => {
        if (sectionData.component) {
            const DynamicComponent = dynamic(
                () => import(`@/app/components/${sectionData.component}`),
                {
                    ssr: false,
                    loading: () => <div>Loading...</div>
                }
            );
            setLoadedComponent(() => DynamicComponent);
        } else {
            setLoadedComponent(null);
        }
    }, [sectionData.component]);

    const handleClose = () => {
        setModalOpen(false);
    };

    if (sectionData.component && !LoadedComponent) {
        return null;
    }

    return (
        <Dialog open={modalOpen} onClose={handleClose} 
        maxWidth={sectionData.dialogProps?.maxWidth || 'md'}
        fullWidth={sectionData.dialogProps?.fullWidth || true}
        scroll={sectionData.dialogProps?.scroll || 'paper'}
         sx={{ ...sectionData.dialogProps?.sx}}>
            <Box p={1} component="div">
                <Box component="div" display="flex" justifyContent="center" alignItems="center" position="relative" height="20px">
                    {sectionData.title && <DialogTitle>
                        <Title sectionData={sectionData} props={props} />
                        </DialogTitle>}
                    {sectionData.subTitle && <DialogTitle>
                        <SubTitle sectionData={sectionData} props={props} />
                        </DialogTitle>}
                    {sectionData.description && <DialogTitle>
                        {transformDescription(sectionData.description, "description", sectionData.trackId, {}, 'h3')}
                    </DialogTitle>}
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: 40,
                            height: 40,
                        }}
                    >
                        <CloseIcon sx={{ fontSize: '24px' }} />
                    </IconButton>
                </Box>
                <DialogContent>
                    {sectionData.uiCompType?.method
                        ? renderSection(sectionData.trackId as string, sectionData, props)
                        : sectionData.component && LoadedComponent
                            ? <LoadedComponent {...sectionData.input} />
                            : null}
                </DialogContent>
            </Box>
        </Dialog>
    );
};
export default OpenPopup;
